import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { Vehicle } from "@/types";

interface UseVehiclesOptions {
  limit?: number;
  category?: string | null;
  city?: string | null;
}

export function useVehicles(options: UseVehiclesOptions = {}) {
  const { limit, category, city } = options;
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from("vehicles")
        .select("*")
        .eq("status", "active")
        .order("rating", { ascending: false });

      if (category) query = query.eq("category", category);
      if (city) query = query.eq("city", city);
      if (limit) query = query.limit(limit);

      const { data, error: queryError } = await query;
      if (queryError) throw queryError;
      setVehicles((data as Vehicle[]) ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load vehicles right now.");
    } finally {
      setLoading(false);
    }
  }, [limit, category, city]);

  useEffect(() => {
    load();
  }, [load]);

  return { vehicles, loading, error, reload: load };
}